/**
 * ClientPricingTiersSection — SystemAdmin per-org graduated (volume tier) pricing.
 *
 * Two independent things live here, and the distinction matters:
 *   1. the ON/OFF switch, which decides whether this client is tier-priced at all
 *   2. this client's OWN band table, which is optional even when the switch is on
 *
 * Switch off  -> flat pricing, exactly as before tiered pricing existed.
 * Switch on, no bands here -> the client prices off the DEFAULT table (Pricing Management).
 * Switch on, bands here    -> this table overrides the default outright, and no further
 *                             discount is applied on top of it.
 *
 * NMVTIS is the only tiered service today. MVR is deliberately not tiered.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layers, Loader2, RefreshCw, Save, ChevronDown } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import { updateClientAdmin } from '../../services/clientsAdminService';
import {
  listClientPricingTiers, saveClientPricingTiers, listDefaultPricingTiers,
  toBandRows, toBandItems, validateBandRows, BandRow,
} from '../../services/pricingTiersService';
import PricingTierBandEditor from './PricingTierBandEditor';

const SERVICE_TYPE = 'NMVTIS';

interface Props {
  clientId: string;
  tieredPricingEnabled: boolean;
  onTieredPricingChange: (enabled: boolean) => void;
  defaultOpen?: boolean;
}

const ClientPricingTiersSection: React.FC<Props> = ({
  clientId, tieredPricingEnabled, onTieredPricingChange, defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState<BandRow[]>([]);
  const [savedRows, setSavedRows] = useState<BandRow[]>([]);
  const [defaultBandCount, setDefaultBandCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingSwitch, setTogglingSwitch] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [mine, defaults] = await Promise.all([
        listClientPricingTiers(clientId, SERVICE_TYPE),
        listDefaultPricingTiers(SERVICE_TYPE),
      ]);
      const asRows = toBandRows(mine);
      setRows(asRows);
      setSavedRows(asRows);
      setDefaultBandCount(defaults.filter((d) => d.isActive).length);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(savedRows),
    [rows, savedRows],
  );

  // The switch saves on its own, immediately — it is a billing control and leaving it
  // pending behind a Save button invites someone to flip it and walk away thinking it took.
  const handleToggle = async (enabled: boolean) => {
    setTogglingSwitch(true);
    setError('');
    try {
      await updateClientAdmin(clientId, { tieredPricingEnabled: enabled });
      onTieredPricingChange(enabled);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setTogglingSwitch(false);
    }
  };

  const handleSave = async () => {
    const validationError = validateBandRows(rows);
    if (validationError) { setError(validationError); return; }

    setSaving(true);
    setError('');
    try {
      const updated = await saveClientPricingTiers(clientId, SERVICE_TYPE, toBandItems(rows));
      const asRows = toBandRows(updated);
      setRows(asRows);
      setSavedRows(asRows);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const activeBandCount = rows.filter((r) => r.isActive).length;

  const pricingSummary = !tieredPricingEnabled
    ? 'Tiered pricing is off — this client bills at flat pricing, unlike the rest of the book.'
    : activeBandCount > 0
      ? `This client's own ${activeBandCount}-band table applies, overriding the default table.`
      : defaultBandCount > 0
        ? `No bands set here, so this client prices off the default ${defaultBandCount}-band table.`
        : 'Tiered pricing is on, but neither this client nor the default table has bands — so billing stays flat.';

  return (
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
      <div
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between px-6 py-4 bg-black/[0.02] hover:bg-black/[0.04] transition-colors cursor-pointer ${open ? 'border-b border-black/[0.08]' : ''}`}
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${open ? '' : '-rotate-90'}`} />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#c41e3a]" /> Volume Tier Pricing
          </h2>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tieredPricingEnabled ? 'bg-[#c41e3a]/10 text-[#c41e3a]' : 'bg-black/[0.06] text-[#64748b]'}`}>
            {tieredPricingEnabled ? 'On' : 'Off'}
          </span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); load(); }} className="text-xs text-[#64748b] hover:text-[#c41e3a] inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {open && (
        <div className="px-6 py-5">
          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox" checked={tieredPricingEnabled} disabled={togglingSwitch}
              onChange={(e) => handleToggle(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium text-[#0f172a]">
                Bill this client on volume tiers (NMVTIS)
                {togglingSwitch && <Loader2 className="w-3.5 h-3.5 animate-spin inline ml-2 text-[#94a3b8]" />}
              </span>
              <span className="block text-xs text-[#64748b] mt-0.5">
                Saves immediately. On is the standard arrangement — switching it off holds this client
                on flat pricing instead. MVR is never tiered and is unaffected either way.
              </span>
            </span>
          </label>

          <div className="mb-4 p-3 rounded-lg bg-black/[0.02] border border-black/[0.06] text-sm text-[#475569]">
            {pricingSummary}
          </div>

          {error && <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}

          <p className="text-xs text-[#64748b] mb-3">
            Bands below are this client's own negotiated table. Leave it empty to use the default table.
            A client table wins outright — no discount is applied on top of it.
          </p>

          {loading ? (
            <div className="py-8 text-center text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading tiers…</div>
          ) : (
            <PricingTierBandEditor rows={rows} onChange={setRows} disabled={saving} />
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={handleSave} disabled={!dirty || saving || loading}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save bands
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPricingTiersSection;
